import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Lock, Bell, FileText, Clock } from 'lucide-react';

interface ApprovalConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policyName: string;
  versionNumber?: number;
  onConfirm: () => void;
  isLoading?: boolean;
}

export function ApprovalConfirmationDialog({
  open,
  onOpenChange,
  policyName,
  versionNumber,
  onConfirm,
  isLoading = false,
}: ApprovalConfirmationDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Approve and Lock This Policy?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p className="text-muted-foreground">Once approved:</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Lock className="h-4 w-4 mt-0.5 shrink-0 text-teal-600" />
                  <span>This policy will be locked and cannot be edited</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="h-4 w-4 mt-0.5 shrink-0 text-teal-600" />
                  <span>A new version will be required for any changes</span>
                </li>
                <li className="flex items-start gap-2">
                  <Bell className="h-4 w-4 mt-0.5 shrink-0 text-teal-600" />
                  <span>All assigned staff will be notified to acknowledge</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="h-4 w-4 mt-0.5 shrink-0 text-teal-600" />
                  <span>The approval will be timestamped and immutable</span>
                </li>
              </ul>
              <p className="text-sm font-medium text-foreground pt-2">
                Are you sure you want to approve "{policyName}"
                {versionNumber ? ` version ${versionNumber}` : ''}?
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isLoading}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            <Lock className="h-4 w-4 mr-1.5" />
            {isLoading ? 'Approving...' : 'Approve & Lock'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
